import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Free shipping bar component that shows progress towards free shipping threshold.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} message
 * @property {HTMLElement} progressBar
 * @property {HTMLElement} progressContainer
 *
 * @extends {Component<Refs>}
 */
class FreeShippingBar extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    // First, use the server-side data for immediate display
    const threshold = parseFloat(this.getAttribute('threshold') || '0') || 0;
    const totalPrice = parseFloat(this.getAttribute('total-price') || '0') || 0;
    const reachedMessage = this.getAttribute('reached-message') || '';
    const unreachedMessage = this.getAttribute('unreached-message') || '';
    
    // console.log('Free shipping bar initialized:', { threshold, totalPrice, reachedMessage, unreachedMessage });
    
    this.#updateProgress(threshold, totalPrice, reachedMessage, unreachedMessage);
    
    // Then fetch fresh cart data to ensure accuracy (with a small delay to allow cart updates to process)
    setTimeout(() => {
      this.#fetchAndUpdateProgress();
    }, 100);
    
    // Listen for cart update events
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Fetches current cart data and updates the progress bar
   */
  #fetchAndUpdateProgress = () => {
    // console.log('Fetching fresh cart data...');
    fetch('/cart.js')
      .then(response => response.json())
      .then(cartData => {
        // console.log('Cart data received:', cartData);
        
        // Recalculate the total price for shipping items
        let calculatedTotalPrice = 0;
        
        for (const lineItem of cartData.items) {
          if (lineItem.requires_shipping) {
            calculatedTotalPrice += lineItem.final_line_price;
          }
        }

        // Remove cart level discounts
        let totalCartDiscount = 0;
        for (const discountApplication of cartData.cart_level_discount_applications) {
          totalCartDiscount += discountApplication.total_allocated_amount;
        }
        
        calculatedTotalPrice -= totalCartDiscount;
        
        // console.log('Calculated total price:', calculatedTotalPrice, 'Cart level discounts:', totalCartDiscount);

        // Update the component with fresh data
        const threshold = parseFloat(this.getAttribute('threshold') || '0') || 0;
        const reachedMessage = this.getAttribute('reached-message') || '';
        const unreachedMessage = this.getAttribute('unreached-message') || '';
        
        this.#updateProgress(threshold, calculatedTotalPrice, reachedMessage, unreachedMessage);
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
      });
  };

  /**
   * Handles cart update events to refresh the progress bar
   * @param {CartUpdateEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // console.log('Cart update event received, fetching fresh data...');
    // Add a small delay to ensure cart data is fully updated
    setTimeout(() => {
      this.#fetchAndUpdateProgress();
    }, 200);
  };

  /**
   * Updates the progress bar and message based on cart total
   * @param {number} threshold - Free shipping threshold amount
   * @param {number} totalPrice - Current cart total
   * @param {string} reachedMessage - Message when threshold is reached
   * @param {string} unreachedMessage - Message when threshold is not reached
   */
  #updateProgress(threshold, totalPrice, reachedMessage, unreachedMessage) {
    const thresholdInCents = threshold * 100;
    const totalPriceInCents = totalPrice;
    
    // console.log('Updating progress:', { thresholdInCents, totalPriceInCents, threshold, totalPrice });
    
    // Safety check: ensure refs exist before using them
    if (!this.refs.progressBar || !this.refs.progressContainer || !this.refs.message) {
      console.error('Required refs not found:', {
        progressBar: !!this.refs.progressBar,
        progressContainer: !!this.refs.progressContainer,
        message: !!this.refs.message
      });
      
      // Retry after a short delay if refs aren't ready
      setTimeout(() => {
        this.#updateProgress(threshold, totalPrice, reachedMessage, unreachedMessage);
      }, 50);
      return;
    }
    
    if (totalPriceInCents >= thresholdInCents) {
      // Threshold reached
      this.refs.message.innerHTML = reachedMessage;
      //this.refs.progressContainer.classList.add('progress-hidden');
      // Ensure progress bar shows 100% when threshold is reached
      this.refs.progressBar.style.width = '100%';
      // Add celebration animation
      this.refs.progressBar.classList.add('threshold-reached');
      // console.log('Threshold reached - set width to 100%');
      
      // Remove the animation class after animation completes
      setTimeout(() => {
        this.refs.progressBar.classList.remove('threshold-reached');
      }, 600);
    } else {
      // Threshold not reached
      const remaining = thresholdInCents - totalPriceInCents;
      const remainingFormatted = (remaining / 100).toFixed(2);
      const message = unreachedMessage.replace('{{ amount }}', remainingFormatted);
      
      // Smooth message update with fade effect
      this.refs.message.style.opacity = '0';
      setTimeout(() => {
        this.refs.message.innerHTML = message;
        this.refs.message.style.opacity = '1';
      }, 150);
      
      //this.refs.progressContainer.classList.remove('progress-hidden');
      
      // Calculate and animate progress
      const percentage = Math.min((totalPriceInCents / thresholdInCents) * 100, 100);
      // console.log('Progress percentage:', percentage, 'Total price:', totalPriceInCents, 'Threshold:', thresholdInCents);
      
      // Ensure the progress bar is visible and has the correct width
      const widthValue = `${percentage}%`;
      // console.log('Setting progress bar width to:', widthValue);
      
      // Add loading state
      this.refs.progressBar.classList.add('updating');
      
      // Set the width with smooth animation
      this.refs.progressBar.style.width = widthValue;
      
      // Remove loading state after animation completes
      setTimeout(() => {
        this.refs.progressBar.classList.remove('updating');
      }, 800);
      
      // Debug: Verify the width was set
      // console.log('Progress bar width after setting:', this.refs.progressBar.style.width);
    }
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
} 